// @flow
import { CalendarOutlined } from '@ant-design/icons'
import zentakeApi from 'api'
import spinner from 'assets/spinner.svg'
import PaperPlane from 'components/PaperPlane'
import OfficeUsePrompt from 'components/SendResource/OfficeUsePrompt'
import SendResourceModalContactField from 'components/SendResource/SendResourceModalContactField'
import useSendForm from 'components/SendResource/useSendForm'
import * as React from 'react'
import { withRouter } from 'react-router-dom'
import { toast } from 'react-toastify'
import type { Customer } from 'types/customer'
import type { Message } from 'types/message'
import type { TeamDetails } from 'types/team_details'
import Ui from 'ui'
import Flex from 'ui/atoms/Flex'
import FlexBox from 'ui/atoms/FlexBox'
import Space from 'ui/atoms/Space'
import { hasText } from 'utils/helpers'
import { ZentakeContext } from 'utils/ZentakeContext'

import { QUERIES } from '@/enums/queries'
import { loadCustomersToSelect } from '@/queries/customers/services/loadCustomersToSelect'
import Button from '@/ui/atoms/Button'
import { FormAsyncSelect } from '@/ui/form/antd/FormAsyncSelect'
import Modal from '@/ui/Modal'
import DropdownButton from '@/ui/molecules/DropdownButton'

import ScheduleSendPrompt from './ScheduleSendPrompt'
import SendResourceModalDOBField from './SendResourceModalDOBField'

type Props = {
  showScheduleSendButton?: boolean,
  afterSelect?: () => Promise<void>,
  customActionIcon?: React.Node,
  dropdownDisabled?: boolean,
  dropdownLabel: string | React.Element<any>,
  dropdownPlaceholder: string,
  isPacket?: boolean,
  isVerified: boolean,
  isFetching?: boolean,
  isDocument?: boolean,
  heading: string,
  subheading: string,
  loading: boolean,
  onHide: () => void,
  customActionText?: string,
  onMenuScrollToBottom?: () => void,
  onType?: (string) => void,
  forceDob?: boolean,
  options: Array<{
    label: string,
    value: string,
    hasOfficeUseQuestions: boolean,
  }>,
  sendResource: ({
    personToUse: Customer,
    is_waiting: boolean,
    email: string,
    id: string,
    messages: Array<Message>,
    setMessages: (Array<Message>) => void,
    teamDetails: ?TeamDetails,
    phone: string,
    willUseEmail: boolean,
    selectedOption: { label: string, value: string, type: string },
    scheduledDate: ?string,
  }) => Promise<void>,
}

type InjectedProps = {
  history: {
    push: (path: string) => void,
  },
}

function getOfficeUsePath(
  isPacket: boolean,
  isDocument: boolean,
  responseId: string,
  messageId: string
) {
  if (isPacket) {
    return `/fillPacketOfficeUseQuestions/${responseId}?messageId=${messageId}`
  }

  if (isDocument) {
    return `/pdf/${responseId}/office-use?messageId=${messageId}`
  }

  return `/fillFormOfficeUseQuestions/${responseId}?messageId=${messageId}`
}

const getSelectOptionByPerson = (person, type) => {
  if (!person) return
  if (type === 'full_name') {
    return {
      label: [person.first_name, person.middle_name, person.last_name]
        .filter(Boolean)
        .join(' '),
      value: String(person.id),
    }
  }
  if (type === 'email') {
    return {
      label: person.email,
      value: person.email,
    }
  }
  if (type === 'phone') {
    return {
      label: person.phone_number,
      value: person.phone_number,
    }
  }
}
function SendResourceModal({
  afterSelect,
  customActionText,
  heading,
  forceDob,
  customActionIcon,
  dropdownDisabled,
  dropdownLabel,
  dropdownPlaceholder,
  isFetching,
  loading,
  isVerified,
  onType,
  history,
  isPacket,
  onHide,
  subheading,
  onMenuScrollToBottom,
  options,
  sendResource,
  showScheduleSendButton,
}: Props & InjectedProps): React.Node {
  const { messages, setMessages, teamDetails, reloadMessages } =
    React.useContext(ZentakeContext)

  const [isSubmitting, setSubmitting] = React.useState(false)
  const [showOfficeUseConfirmation, setShowOfficeUseConfirmation] =
    React.useState(false)
  const [showScheduleSend, setShowScheduleSend] = React.useState(false)

  const [personToSendForm, setPersonToSendForm] = React.useState(null)

  const {
    email,
    person,
    personToAdd,
    willUseEmail,
    phone,
    id,
    setEmail,
    validateEmail,
    setPhone,
    validatePhone,
    hasFormError,
    formError,
    formErrorLabel,
    onPersonSelected,
    onClearSelectedPerson,
    onChangeEmailToggle,
    onFormSelected,
    validateForm,
    dob,
    setDob,
    validateDob,
    selectedOption,
    scheduledDate,
    setScheduledDate,
  } = useSendForm({ isPacket, forceDob })

  // #region Submit
  const setContactToSendForm = React.useCallback(async () => {
    // Brand-new user
    if (
      !!personToAdd?.first_name &&
      !!personToAdd?.last_name &&
      (!!email || !!phone || !!dob)
    ) {
      return await zentakeApi.getOrCreateCustomer({
        firstName: personToAdd.first_name,
        lastName: personToAdd.last_name,
        middleName: personToAdd.middle_name,
        email,
        phone,
        dateOfBirth: dob,
        resourceType: isPacket ? 'packet' : 'form',
        resourceId: id,
      })
    }

    // Same user, new email or phone
    if (
      hasText(person?.first_name) &&
      hasText(person?.last_name) &&
      (person?.email !== email || person?.phone !== phone)
    ) {
      if (person?.id) {
        const dataToUpdate: { email?: string, phone_number?: string } = {}

        if (email) {
          dataToUpdate.email = email
        }
        if (phone) {
          dataToUpdate.phone_number = phone
        }

        return await zentakeApi.updateClient(person.id, dataToUpdate)
      }
      return await zentakeApi.getOrCreateCustomer({
        firstName: person.first_name,
        middleName: person.middle_name,
        lastName: person.last_name,
        email,
        phone,
        resourceType: isPacket ? 'packet' : 'form',
        resourceId: id,
      })
    }
    // User is already created
    return person
  }, [
    dob,
    email,
    id,
    isPacket,
    person,
    personToAdd.first_name,
    personToAdd.last_name,
    personToAdd.middle_name,
    phone,
  ])

  const doSendResource = React.useCallback(
    async (personToUse, is_waiting) => {
      if (!personToUse) return

      return await sendResource({
        personToUse,
        is_waiting,
        email,
        id,
        messages,
        setMessages,
        teamDetails,
        phone,
        willUseEmail,
        selectedOption,
        scheduledDate,
      })
    },
    [
      sendResource,
      email,
      id,
      messages,
      setMessages,
      teamDetails,
      phone,
      willUseEmail,
      selectedOption,
      scheduledDate,
    ]
  )

  // #region Office Use
  const onIgnoreOfficeQuestions = React.useCallback(async () => {
    try {
      setShowOfficeUseConfirmation(false)
      setSubmitting(true)
      await doSendResource(personToSendForm, false)
      setSubmitting(false)
      onHide()
      reloadMessages()
    } catch (error) {
      setSubmitting(false)
    }
  }, [doSendResource, personToSendForm, onHide, reloadMessages])

  const prepareOfficeUseQuestions = React.useCallback(async () => {
    setShowOfficeUseConfirmation(false)
    setSubmitting(true)
    const newMessage = await doSendResource(personToSendForm, true)
    const responseId = isPacket
      ? (newMessage?.packet_response?.id ?? '')
      : (newMessage?.response?.id ?? '')

    const messageId = newMessage?.id ?? ''
    const isDocument = Boolean(newMessage?.document)
    setSubmitting(false)
    onHide()
    reloadMessages()
    history.push(
      getOfficeUsePath(Boolean(isPacket), isDocument, responseId, messageId)
    )
  }, [
    doSendResource,
    personToSendForm,
    isPacket,
    onHide,
    reloadMessages,
    history,
  ])

  const preHandleSubmit = React.useCallback(async () => {
    const isFormValid = await validateForm()
    if (!isFormValid) {
      return false
    }

    if (!isVerified) {
      toast.warning('Verify Email')
      return false
    }

    return true
  }, [validateForm, isVerified])

  const handleSubmit = React.useCallback(async () => {
    try {
      setSubmitting(true)

      const isFormValid = await preHandleSubmit()
      if (!isFormValid) {
        setSubmitting(false)
        return
      }

      const personToSend = await setContactToSendForm()
      setPersonToSendForm(personToSend)

      if (!selectedOption) {
        setSubmitting(false)
        return
      }

      if (selectedOption.hasOfficeUseQuestions) {
        setShowOfficeUseConfirmation(true)
        setSubmitting(false)
        return
      }

      await doSendResource(personToSend, false)
      setSubmitting(false)
      onHide()
      reloadMessages()
    } catch (error) {
      setSubmitting(false)
    }
  }, [
    preHandleSubmit,
    setContactToSendForm,
    selectedOption,
    doSendResource,
    onHide,
    reloadMessages,
  ])

  const onChange = React.useCallback(
    async (option) => {
      onFormSelected(option?.value || '', option)

      if (afterSelect) {
        await afterSelect()
      }
    },
    [afterSelect, onFormSelected]
  )

  const handleScheduleSend = async () => {
    const isFormValid = await preHandleSubmit()
    if (isFormValid) {
      setShowScheduleSend(true)
    }
  }

  return (
    <Modal
      centered
      open
      footer={() => undefined}
      onCancel={onHide}
      title={heading}
      width="700px"
    >
      <p className="mb-4 text-base text-gray-500">{subheading}</p>
      <form>
        <Space size="large">
          <div className="w-full pb-1">
            <FormAsyncSelect
              createOption
              hasLocked
              labelInValue
              notFoundContentAction
              errorLabel={formErrorLabel.full_name}
              fetchOptions={loadCustomersToSelect}
              fieldNames={{ groupLabel: 'searchBy' }}
              hasError={formError.full_name}
              label="Full Name"
              onChange={onPersonSelected}
              onClear={onClearSelectedPerson}
              optionLabelProp="raw_label"
              placeholder="Full Name"
              queryKey={QUERIES.CUSTOMERS.LOAD_TO_SELECT}
              searchKey="search"
              value={
                personToAdd?.full_name
                  ? personToAdd.full_name
                  : getSelectOptionByPerson(person, 'full_name')
              }
            />
          </div>
          <Flex $horizontalSpacing="medium">
            {forceDob ? (
              <SendResourceModalDOBField
                dateOfBirth={dob}
                formError={formError}
                formErrorLabel={formErrorLabel}
                setDob={setDob}
                validateDateOfBirth={validateDob}
              />
            ) : (
              <SendResourceModalContactField
                email={email}
                formError={formError}
                formErrorLabel={formErrorLabel}
                onChangeEmailToggle={onChangeEmailToggle}
                phone={phone}
                setEmail={setEmail}
                setPhone={setPhone}
                validateEmail={validateEmail}
                validatePhone={validatePhone}
                willUseEmail={willUseEmail}
              />
            )}
            <FlexBox>
              <Ui.Select
                showOptions
                disabled={loading || dropdownDisabled}
                errorLabel={formErrorLabel?.form}
                hasError={formError?.form}
                isFetching={isFetching}
                label={dropdownLabel}
                loading={loading}
                onChange={onChange}
                onMenuScrollToBottom={onMenuScrollToBottom}
                onType={onType}
                options={selectedOption ? [] : options}
                placeholder={dropdownPlaceholder}
              />
            </FlexBox>
          </Flex>
          <div className="flex w-full justify-end">
            {showScheduleSendButton ? (
              <DropdownButton
                $primary
                disabled={hasFormError || isSubmitting}
                items={[
                  {
                    key: 'schedule',
                    label: 'Schedule Send',
                    onClick: handleScheduleSend,
                    icon: <CalendarOutlined />,
                  },
                ]}
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <img alt="spinner" src={spinner} />
                ) : (
                  <>
                    {customActionIcon !== undefined ? (
                      customActionIcon
                    ) : (
                      <PaperPlane />
                    )}
                  </>
                )}
                {customActionText || 'Send'}
              </DropdownButton>
            ) : (
              <Button
                $primary
                disabled={hasFormError || isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  <img alt="spinner" src={spinner} />
                ) : (
                  <>
                    {customActionIcon !== undefined ? (
                      customActionIcon
                    ) : (
                      <PaperPlane />
                    )}
                  </>
                )}
                {customActionText || 'Send'}
              </Button>
            )}
          </div>
        </Space>
      </form>
      {showOfficeUseConfirmation && (
        <OfficeUsePrompt
          formType={isPacket ? 'packet' : 'form'}
          onCancel={onIgnoreOfficeQuestions}
          onClose={() => setShowOfficeUseConfirmation(false)}
          onConfirm={prepareOfficeUseQuestions}
        />
      )}
      {showScheduleSend && (
        <ScheduleSendPrompt
          isPacket={isPacket}
          onClose={() => {
            setShowScheduleSend(false)
          }}
          onConfirm={handleSubmit}
          onScheduleAtChange={setScheduledDate}
          scheduleAt={scheduledDate}
        />
      )}
    </Modal>
  )
}

const EnhancedSendResource: React.ComponentType<Props> =
  withRouter(SendResourceModal)

export default EnhancedSendResource
